import { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import {
  Image, Video, Film, Scissors, Clock, Users, Settings, LayoutDashboard,
  LogOut, ChevronLeft, ChevronRight, Zap, Layers, BarChart3, LayoutTemplate
} from 'lucide-react';
import { Button } from './ui/button';
import { Separator } from './ui/separator';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';

const navItems = [
  { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
  { to: '/generate/image', icon: Image, label: 'Image Generation' },
  { to: '/generate/video', icon: Video, label: 'Video Generation' },
  { to: '/generate/image-to-video', icon: Film, label: 'Image to Video' },
  { to: '/generate/video-edit', icon: Scissors, label: 'Video Editing' },
  { to: '/generate/amazon-aplus', icon: LayoutTemplate, label: 'Amazon A+' },
  { to: '/batch', icon: Layers, label: 'Batch Queue' },
  { to: '/history', icon: Clock, label: 'History' },
];

const adminItems = [
  { to: '/admin/dashboard', icon: BarChart3, label: 'Admin Dashboard' },
  { to: '/admin/users', icon: Users, label: 'User Access' },
  { to: '/settings', icon: Settings, label: 'Settings' },
];

export const Sidebar = () => {
  const [collapsed, setCollapsed] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const NavItem = ({ to, icon: Icon, label }) => {
    const content = (
      <NavLink
        to={to}
        data-testid={`nav-${label.toLowerCase().replace(/\s+/g, '-')}`}
        className={({ isActive }) =>
          `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors ${
            isActive
              ? 'text-white bg-blue-600/10 border-l-2 border-blue-500'
              : 'text-slate-400 hover:text-white hover:bg-slate-800/50'
          } ${collapsed ? 'justify-center px-2' : ''}`
        }
      >
        <Icon size={20} strokeWidth={1.5} className="flex-shrink-0" />
        {!collapsed && <span>{label}</span>}
      </NavLink>
    );

    if (collapsed) {
      return (
        <Tooltip>
          <TooltipTrigger asChild>{content}</TooltipTrigger>
          <TooltipContent side="right"><p>{label}</p></TooltipContent>
        </Tooltip>
      );
    }
    return content;
  };

  return (
    <TooltipProvider delayDuration={0}>
      <aside
        data-testid="sidebar"
        className={`glass-sidebar h-screen fixed left-0 top-0 z-40 flex flex-col transition-all duration-300 ${
          collapsed ? 'w-20' : 'w-64'
        }`}
      >
        <div className={`flex items-center h-16 px-4 ${collapsed ? 'justify-center' : 'gap-3'}`}>
          <div className="w-8 h-8 rounded-md bg-blue-600 flex items-center justify-center flex-shrink-0">
            <Zap size={18} className="text-white" />
          </div>
          {!collapsed && (
            <span className="font-semibold text-lg tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
              SND AI
            </span>
          )}
        </div>

        <Separator className="bg-slate-800" />

        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
          {navItems.map((item) => (
            <NavItem key={item.to} {...item} />
          ))}

          {user?.role === 'admin' && (
            <>
              <Separator className="bg-slate-800 my-3" />
              <p className={`text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2 ${collapsed ? 'text-center' : 'px-3'}`}>
                {collapsed ? '---' : 'Admin'}
              </p>
              {adminItems.map((item) => (
                <NavItem key={item.to} {...item} />
              ))}
            </>
          )}
        </nav>

        <div className="px-3 pb-4 space-y-2">
          <Separator className="bg-slate-800" />
          {!collapsed && (
            <div className="px-3 py-2">
              <p className="text-sm font-medium text-slate-200 truncate">{user?.name}</p>
              <p className="text-xs text-slate-500 truncate">{user?.email}</p>
            </div>
          )}
          <Button
            variant="ghost"
            data-testid="logout-btn"
            onClick={handleLogout}
            className={`w-full text-slate-400 hover:text-red-400 hover:bg-red-500/10 ${collapsed ? 'justify-center px-2' : 'justify-start'}`}
          >
            <LogOut size={18} strokeWidth={1.5} />
            {!collapsed && <span className="ml-2">Logout</span>}
          </Button>
        </div>

        <button
          data-testid="sidebar-toggle"
          onClick={() => setCollapsed(!collapsed)}
          className="absolute -right-3 top-20 w-6 h-6 bg-slate-800 border border-slate-700 rounded-full flex items-center justify-center text-slate-400 hover:text-white hover:bg-slate-700 transition-colors"
        >
          {collapsed ? <ChevronRight size={14} /> : <ChevronLeft size={14} />}
        </button>
      </aside>
    </TooltipProvider>
  );
};
